import React, { type CSSProperties } from 'react'

import classnames from 'classnames'
import { isEmpty } from 'lodash-es'
import {
  getPersonIdentifier,
  getPersonLocation,
  // @ts-expect-error — importing js
} from '../../../Audience/Person/selectors'
// @ts-expect-error — importing js
import { FlagComponent, DefaultAvatarComponent, ImageComponent } from '..'
// @ts-expect-error — importing js
import { humanizeSeconds } from '../../utils'

import './PersonIdentityComponent.scss'
import type { Person, Profile } from '../../hooks/useAudienceQuery'

type AvatarSize = 'normal' | 'large' | 'small'

const sizeMap = {
  large: 60,
  normal: 40,
  small: 32,
}

const renderFlag = (profile: Profile) => {
  if (profile.countryCode) {
    return <FlagComponent countryCode={profile.countryCode.toLowerCase()} />
  }

  return null
}

const renderAvatar = (person: Person, avatarSize: AvatarSize) => {
  const firstName = person.profile.firstname ?? ''
  const lastName = person.profile.lastname ?? ''
  const identifier = `${firstName} ${lastName}`

  const dimensions = sizeMap[avatarSize]
  const defaultAvatar = (
    <DefaultAvatarComponent
      identifier={identifier}
      width={dimensions}
      height={dimensions}
    />
  )

  if (isEmpty(person.profile.avatar)) {
    return defaultAvatar
  }

  return (
    <ImageComponent
      className="person-avatar"
      src={person.profile.avatar}
      alt="avatar"
      backup={defaultAvatar}
    />
  )
}

const renderSubline = (person: Person, preferCorporateDetails?: boolean) => {
  const { role, company } = person.profile

  if (preferCorporateDetails && !isEmpty(company)) {
    return role ? `${role} • ${company}` : company
  }

  return (
    <>
      {renderFlag(person.profile)}
      <div className="locator">
        {person.email || getPersonLocation(person.profile)}
      </div>
    </>
  )
}

const renderStats = (person: Person, style: CSSProperties = {}) => {
  const line = `${person.timelines.length || 0} views • 
  ${
    humanizeSeconds(person.timeline_engagement, false) || '0m'
  } total engagement`

  return (
    <div className="stats-line" style={style}>
      {line}
    </div>
  )
}

interface PersonIdentityComponentProps {
  person: Person
  avatarSize?: 'normal' | 'large' | 'small'
  preferCorporateDetails?: boolean
  showStats?: boolean
  theme?: 'pv-theme' | ''
  style?: CSSProperties
}

const PersonIdentityComponent = ({
  avatarSize = 'normal',
  theme = '',
  person,
  preferCorporateDetails,
  showStats,
  style,
}: PersonIdentityComponentProps) => {
  const className = classnames(
    `person-identity-component avatar-${avatarSize}`,
    { [theme]: theme }
  )

  return (
    <div className={className}>
      <div className="avatar-wrapper">{renderAvatar(person, avatarSize)}</div>
      <div className="person-details">
        <div className="identifier" style={style}>
          {getPersonIdentifier(person)}
        </div>
        <div className="person-subline" style={style}>
          {renderSubline(person, preferCorporateDetails)}
        </div>
        {showStats && renderStats(person, style)}
      </div>
    </div>
  )
}

export default PersonIdentityComponent
